///
/// EarthBrowser
///
/// Created by Matt Giger on 10/26/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Sync-only layer for the USGS Slab2 subduction geometry: one static
/// Arrow checkpoint into SQLite after the visible layers have loaded. Draws
/// nothing; the earthquake cutout queries the table.
///

import { type Database } from "@/earthbrowser/database/Database"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { DatasetLayer, type ArrowRow, type DatasetFeature, type DatasetManifest, type DatasetSceneObject, type DatasetSyncConfig } from "../../DatasetLayer"
import { Slab2Table, type Slab2Row } from "./Slab2Table"

export class Slab2DatasetLayer extends DatasetLayer<DatasetFeature> {
	constructor(context: EarthBrowserContext) {
		super(context, "slab2")
		this.visible = false
	}

	public getSyncConfig(): DatasetSyncConfig {
		return {
			datasetId: "slab2",
			pollIntervalMs: 0,
			needsSync: true,
			supportsDelta: false,
			deferInitialSync: true,
		}
	}

	public async createTable(db: Database): Promise<void> {
		await Slab2Table.create(db)
	}

	public override async getStoredRowCount(_datasetId: string): Promise<number | null> {
		return Slab2Table.getCount()
	}

	public async applyCheckpoint(manifest: DatasetManifest, downloadArrowRows: (url: string) => Promise<ArrowRow[]>): Promise<void> {
		const shard = manifest.checkpoint.shards[0]
		if (!shard) return
		const rows = await downloadArrowRows(this.resolveUrl(shard.url, shard.path)) as Array<Record<string, unknown>>
		const num = (v: unknown): number | null => {
			const n = Number(v)
			return Number.isFinite(n) ? n : null
		}
		const parsed: Slab2Row[] = rows.map(r => ({
			zone: typeof r.zone === "string" ? r.zone : "",
			lon: Number(r.lon),
			lat: Number(r.lat),
			depth_km: Number(r.depth_km),
			dip_deg: num(r.dip_deg),
			strike_deg: num(r.strike_deg),
		}))
		await Slab2Table.replaceAll(parsed)
	}

	public createSceneObject(_feature: DatasetFeature): DatasetSceneObject<DatasetFeature> | undefined {
		return undefined
	}
}
